import { Controller } from "@hotwired/stimulus"

// Filters From/To location dropdowns on each trip leg to only allow
// combinations that have a configured route duration. Fetches the route
// list once from /orders/route_durations.json (module-level cache) and
// disables invalid options on every change. Auto-selects when only one
// valid option remains. Graceful fallback: if no route durations are
// configured, all options stay enabled.
//
// Also auto-fills the hidden estimated_duration_minutes field when a
// valid pair is selected (the duration comes from the route config).

let cachedRouteDurations = null
let fetchPromise = null

export default class extends Controller {
  static targets = ["from", "to", "duration"]
  static values = { url: String }

  connect() {
    this.loadRouteDurations().then(() => {
      this.filterTo()
      this.filterFrom()
    })
  }

  async loadRouteDurations() {
    if (cachedRouteDurations) return
    if (fetchPromise) { await fetchPromise; return }

    fetchPromise = (async () => {
      try {
        const res = await fetch(this.urlValue, { headers: { Accept: "application/json" } })
        if (!res.ok) { cachedRouteDurations = []; return }
        const body = await res.json()
        cachedRouteDurations = body.route_durations || []
      } catch (_e) {
        cachedRouteDurations = []
      }
    })()

    await fetchPromise
  }

  filterTo() {
    if (!cachedRouteDurations || cachedRouteDurations.length === 0) return
    const fromId = this.fromTarget.value
    this.applyFilter(this.toTarget, fromId)
    this.fillDuration()
  }

  filterFrom() {
    if (!cachedRouteDurations || cachedRouteDurations.length === 0) return
    const toId = this.toTarget.value
    this.applyFilter(this.fromTarget, toId)
    this.fillDuration()
  }

  applyFilter(selectEl, partnerId) {
    const validIds = this.validPartnersFor(partnerId)
    let enabledCount = 0
    let lastEnabledValue = null

    for (const option of selectEl.options) {
      if (!option.value) continue // skip blank placeholder
      if (validIds === null) {
        // No filtering — either no partner selected or no routes configured
        option.disabled = false
        enabledCount++
        lastEnabledValue = option.value
      } else {
        const allowed = validIds.has(option.value)
        option.disabled = !allowed
        if (allowed) { enabledCount++; lastEnabledValue = option.value }
      }
    }

    // If the current selection is now disabled, reset to blank
    if (selectEl.value && selectEl.selectedOptions[0]?.disabled) {
      selectEl.value = ""
    }

    // Auto-select when exactly one valid option remains
    if (enabledCount === 1 && lastEnabledValue && selectEl.value !== lastEnabledValue) {
      selectEl.value = lastEnabledValue
      selectEl.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  // Returns a Set of valid partner IDs for the given location, checking
  // both directions (routes are bidirectional). Returns null if partnerId
  // is blank (meaning "no filtering").
  validPartnersFor(partnerId) {
    if (!partnerId) return null

    const ids = new Set()
    for (const rd of cachedRouteDurations) {
      if (rd.from_location_id === partnerId) ids.add(rd.to_location_id)
      if (rd.to_location_id === partnerId) ids.add(rd.from_location_id)
    }
    return ids
  }

  // Auto-fill the hidden estimated_duration_minutes when both From and To
  // match a configured route. Does not overwrite if the field already has
  // a nonzero value (preserving round-tripped Helm data on edit).
  fillDuration() {
    if (!this.hasDurationTarget) return
    if (!cachedRouteDurations || cachedRouteDurations.length === 0) return

    const fromId = this.fromTarget.value
    const toId = this.toTarget.value
    if (!fromId || !toId) return

    // Don't overwrite existing values (round-tripped from Helm on edit)
    const current = parseInt(this.durationTarget.value, 10)
    if (current > 0) return

    const rd = cachedRouteDurations.find(
      (r) => (r.from_location_id === fromId && r.to_location_id === toId) ||
             (r.from_location_id === toId && r.to_location_id === fromId)
    )
    if (rd) this.durationTarget.value = rd.duration_minutes
  }
}
